import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    Snackbar,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
    ReferralLink,
    ReferralLinkStats,
    fetchReferralLinksRequest,
    deleteReferralLinkRequest,
    toggleReferralLinkActiveRequest,
    fetchReferralLinkStatsRequest,
} from '../api/referralLinksApi';
import ReferralLinkDialog from './ReferralLinkDialog';
import { format } from 'date-fns';

const ReferralLinksPage: React.FC = () => {
    const [links, setLinks] = useState<ReferralLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState<ReferralLink | null>(null);
    const [searchText, setSearchText] = useState('');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [statsLink, setStatsLink] = useState<ReferralLink | null>(null);
    const [stats, setStats] = useState<ReferralLinkStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const currentUser = useSelector((state: RootState) => state.auth.user);
    const isAdmin = currentUser?.role === 'ADMIN';

    const loadLinks = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchReferralLinksRequest();
            setLinks(data);
        } catch (error) {
            console.error('Failed to load referral links:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load referral links',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLinks();
    }, [loadLinks]);

    const handleCreate = () => {
        setSelectedLink(null);
        setDialogOpen(true);
    };

    const handleEdit = (link: ReferralLink) => {
        if (!link.canEdit) {
            setSnackbar({
                open: true,
                message: 'You do not have permission to edit this link',
                severity: 'error',
            });
            return;
        }
        setSelectedLink(link);
        setDialogOpen(true);
    };

    const handleDelete = async (link: ReferralLink) => {
        if (!link.canEdit) {
            setSnackbar({
                open: true,
                message: 'You do not have permission to delete this link',
                severity: 'error',
            });
            return;
        }

        if (!window.confirm(`Are you sure you want to delete "${link.title}"?`)) {
            return;
        }

        try {
            await deleteReferralLinkRequest(link.id);
            setSnackbar({
                open: true,
                message: 'Referral link deleted successfully',
                severity: 'success',
            });
            loadLinks();
        } catch (error) {
            console.error('Failed to delete referral link:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete referral link',
                severity: 'error',
            });
        }
    };

    const handleToggleActive = async (link: ReferralLink) => {
        if (!link.canEdit) {
            setSnackbar({
                open: true,
                message: 'You do not have permission to update this link',
                severity: 'error',
            });
            return;
        }

        try {
            await toggleReferralLinkActiveRequest(link.id);
            setSnackbar({
                open: true,
                message: `Link ${link.isActive ? 'deactivated' : 'activated'} successfully`,
                severity: 'success',
            });
            loadLinks();
        } catch (error) {
            console.error('Failed to toggle link status:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update link status',
                severity: 'error',
            });
        }
    };

    const handleCopyLink = (link: ReferralLink) => {
        if (link.fullUrl) {
            navigator.clipboard.writeText(link.fullUrl);
            setSnackbar({
                open: true,
                message: 'Link copied to clipboard',
                severity: 'success',
            });
        }
    };

    const handleCopyWaLink = (link: ReferralLink) => {
        if (link.waMeUrl) {
            navigator.clipboard.writeText(link.waMeUrl);
            setSnackbar({
                open: true,
                message: 'WhatsApp link copied to clipboard',
                severity: 'success',
            });
        }
    };

    const handleCopyCode = (link: ReferralLink) => {
        navigator.clipboard.writeText(link.code);
        setSnackbar({
            open: true,
            message: `Code ${link.code} copied`,
            severity: 'success',
        });
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedLink(null);
    };

    const handleDialogSave = () => {
        setDialogOpen(false);
        setSelectedLink(null);
        loadLinks();
        setSnackbar({
            open: true,
            message: selectedLink ? 'Link updated successfully' : 'Link created successfully',
            severity: 'success',
        });
    };

    const handleViewDetails = async (link: ReferralLink) => {
        setStatsLink(link);
        setStats(null);
        setStatsLoading(true);
        try {
            const data = await fetchReferralLinkStatsRequest(link.id);
            setStats(data);
        } catch (err) {
            console.error('Failed to load stats:', err);
            setSnackbar({ open: true, message: 'Failed to load stats', severity: 'error' });
        } finally {
            setStatsLoading(false);
        }
    };

    const filteredLinks = links.filter(link => {
        const q = searchText.toLowerCase();
        return (
            link.title.toLowerCase().includes(q) ||
            link.linkName.toLowerCase().includes(q) ||
            link.code?.toLowerCase().includes(q) ||
            link.recruiterName?.toLowerCase().includes(q) ||
            link.description?.toLowerCase().includes(q)
        );
    });

    // Render relative expiry text + color. Past expiry → red, <14 days → orange,
    // otherwise neutral. Null `expiresAt` means "no expiry" — render an em-dash.
    const renderExpiresAt = (raw: string | null | undefined) => {
        if (raw === null) {
            return <Typography variant="caption" color="text.secondary">No expiry</Typography>;
        }
        if (!raw) return '-';
        const expires = new Date(raw);
        const diffMs = expires.getTime() - Date.now();
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
            return (
                <Chip
                    label={`Expired ${Math.abs(diffDays)}d ago`}
                    size="small"
                    color="error"
                />
            );
        }
        if (diffDays <= 14) {
            return <Chip label={`in ${diffDays}d`} size="small" color="warning" />;
        }
        return (
            <Typography variant="caption" color="text.secondary">
                in {diffDays}d
            </Typography>
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'linkName',
            headerName: 'Link Name',
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'code',
            headerName: 'Code',
            width: 110,
            renderCell: (params: GridRenderCellParams) => {
                const link = params.row as ReferralLink;
                if (!link.code) return '-';
                return (
                    <Tooltip title="Copy code">
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            onClick={() => handleCopyCode(link)}
                        >
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {link.code}
                            </Typography>
                            <ContentCopyIcon fontSize="inherit" color="action" />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'recruiterName',
            headerName: 'Recruiter',
            width: 150,
            renderCell: (params: GridRenderCellParams) => params.value || '-',
        },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'visitCount',
            headerName: 'Total Clicks',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const link = params.row as ReferralLink;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                            {link.visitCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            ({link.uniqueVisitorCount} unique)
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'linkedWorkersCount',
            headerName: 'Linked Workers',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const count = params.value || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        color={count > 0 ? 'primary' : 'default'}
                        variant={count > 0 ? 'filled' : 'outlined'}
                    />
                );
            },
        },
        ...(isAdmin ? [{
            field: 'creatorName',
            headerName: 'Created By',
            width: 150,
        }] : []),
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 120,
            renderCell: (params: GridRenderCellParams) =>
                format(new Date(params.value), 'MMM dd, yyyy'),
        },
        {
            field: 'expiresAt',
            headerName: 'Expires',
            width: 130,
            renderCell: (params: GridRenderCellParams) => renderExpiresAt(params.value),
        },
        {
            field: 'lastVisitedAt',
            headerName: 'Last Visit',
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return '-';
                const date = new Date(params.value);
                const now = new Date();
                const diffMs = now.getTime() - date.getTime();
                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                
                if (diffHours < 1) {
                    return <Chip label="< 1 hour ago" size="small" color="success" />;
                } else if (diffHours < 24) {
                    return <Chip label={`${diffHours}h ago`} size="small" color="primary" />;
                } else {
                    const diffDays = Math.floor(diffHours / 24);
                    return <Typography variant="caption" color="text.secondary">
                        {diffDays === 1 ? '1 day ago' : `${diffDays} days ago`}
                    </Typography>;
                }
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 220,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const link = params.row as ReferralLink;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Copy public landing link">
                            <IconButton size="small" onClick={() => handleCopyLink(link)}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy WhatsApp deep link (prefilled with code)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyWaLink(link)}
                                    disabled={!link.waMeUrl}
                                >
                                    <WhatsAppIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={link.isActive ? 'Deactivate' : 'Activate'}>
                            <IconButton 
                                size="small" 
                                onClick={() => handleToggleActive(link)}
                                disabled={!link.canEdit}
                            >
                                {link.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        {isAdmin && (
                            <Tooltip title="View Details">
                                <IconButton 
                                    size="small" 
                                    onClick={() => handleViewDetails(link)}
                                    color="info"
                                >
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Edit">
                            <IconButton 
                                size="small" 
                                onClick={() => handleEdit(link)}
                                disabled={!link.canEdit}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton 
                                size="small" 
                                onClick={() => handleDelete(link)}
                                disabled={!link.canEdit}
                                color="error"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h5" component="h1">
                            Referral Links
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Track clicks and linked workers for your referral campaigns
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search links..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 250 }}
                        />
                        <Tooltip title="Refresh">
                            <IconButton onClick={loadLinks} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreate}
                        >
                            Create Link
                        </Button>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredLinks.length === 0 ? (
                    <Alert severity="info">
                        {searchText
                            ? 'No referral links found matching your search.'
                            : 'No referral links created yet. Click "Create Link" to get started.'}
                    </Alert>
                ) : (
                    <DataGrid
                        rows={filteredLinks}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                        sx={{
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                            },
                        }}
                    />
                )}
            </Paper>

            <ReferralLinkDialog
                open={dialogOpen}
                link={selectedLink}
                onClose={handleDialogClose}
                onSave={handleDialogSave}
            />

            <Dialog
                open={!!statsLink}
                onClose={() => { setStatsLink(null); setStats(null); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {statsLink?.title}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace' }}>
                        {statsLink?.code} · /contact/{statsLink?.linkName}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {statsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : stats ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Visits</Typography>
                                    <Typography variant="h5">{stats.visits}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {stats.uniqueVisitors} unique
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Conversations started</Typography>
                                    <Typography variant="h5">{stats.conversationsStarted}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Linked workers</Typography>
                                    <Typography variant="h5">{stats.linkedWorkers}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Worker conversion</Typography>
                                    <Typography variant="h5">
                                        {(stats.workerConversionRate * 100).toFixed(0)}%
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Sessions by confidence
                                </Typography>
                                {Object.keys(stats.sessionsByConfidence).length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">No attributed sessions yet.</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {Object.entries(stats.sessionsByConfidence).map(([tier, count]) => (
                                            <Chip
                                                key={tier}
                                                label={`${tier}: ${count}`}
                                                size="small"
                                                color={tier === 'DIRECT_AD' || tier === 'DIRECT' ? 'success' : tier === 'BOT_CAPTURED' ? 'primary' : tier === 'MANUAL' ? 'warning' : 'default'}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            <Divider />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                    First visit: {stats.firstVisitAt ? new Date(stats.firstVisitAt).toLocaleString() : 'Never'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Last visit: {stats.lastVisitAt ? new Date(stats.lastVisitAt).toLocaleString() : 'Never'}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">No data.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setStatsLink(null); setStats(null); }}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReferralLinksPage;